import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Card, Label, TextInput } from "flowbite-react";
import { toast } from "sonner";

import { FETCH_JOB_COMPANY_NAME, FETCH_JOB_NAME, SEND_SINGLE_INTERVIEW } from "../lib/urls";
import Button from "../lib/ui/components/button/Button";

const InterviewPage = ({ resolvedData }) => {
  const { id: jobId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Use resolved data if available (from short URL), otherwise use URL params
  const finalJobId = resolvedData?.job_id || jobId;
  const finalCompanyId = resolvedData?.company_id || queryParams.get("companyId");
  const [jobName, setJobName] = useState(resolvedData?.role_name || "");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [channel, setChannel] = useState("sms");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form Submitted:", formData, channel);

    const promiseResponse = async () => {
      try {
        const response = await fetch(SEND_SINGLE_INTERVIEW, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            job_id: finalJobId,
            candidate: {
              name: formData.name,
              phone: formData.phone,
              email: "",
            },
            channel,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error);
        }
        setSubmitted(true);
        return response;
      } catch (error) {
        console.error("Error sending interview invitation", error);
        throw error;
      }
    };

    toast.promise(promiseResponse(), {
      loading: "Sending interview invite...",
      success: "Interview invite sent successfully!",
      error: "Failed to send interview invite.",
    });
  };
  const handleResubmit = () => {
    setSubmitted(false);
  };
  const [companyName, setCompanyName] = useState(resolvedData?.company_name || "");

  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const response = await fetch(FETCH_JOB_COMPANY_NAME(finalJobId));
        if (!response.ok) {
          throw new Error("Failed to fetch company name");
        }
        const data = await response.json();
        setCompanyName(data.company_name);
      } catch (error) {
        console.error("Error fetching company name:", error);
      }
    };

    if (finalJobId && !resolvedData?.company_name) {
      fetchCompanyName();
    }
  }, [finalJobId, resolvedData]);

  useEffect(() => {
    const fetchJobName = async () => {
      try {
        const response = await fetch(FETCH_JOB_NAME(finalJobId));
        if (!response.ok) {
          throw new Error("Failed to fetch job name");
        }
        const data = await response.json();
        setJobName(data.job_name);
      } catch (error) {
        console.error("Error fetching job name:", error);
      }
    };

    if (finalJobId && !resolvedData?.role_name) {
      fetchJobName();
    }
  }, [finalJobId, resolvedData]);

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50">
      {/* Main Header */}
      <div className="w-full max-w-2xl mx-auto flex flex-col items-start mt-12 mb-6">
        <h1
          className="text-h4 md:text-h2 font-bold mb-2"
          style={{ color: "#881BC8" }}
        >
          Thanks for your interest in {jobName} at {companyName}.
        </h1>
        <h1
          className="text-h4 md:text-h2 font-semibold mb-2"
          style={{ color: "#881BC8" }}
        >
          Let's get you started!
        </h1>
      </div>
      {/* Card Section */}
      <div className="w-full max-w-2xl mx-auto">
        <Card>
          {submitted ? (
            <div className="flex flex-col ">
              <h2 className="text-h4 text-gray-600 font-bold mb-4">
                You're all set!
              </h2>
              <p className="mb-4 text-label text-gray-700">
                We've sent you a message to start answering a few quick screening questions. Simply reply to the message to complete the process at your convenience.
              </p>
              <p className="mb-4 text-label text-gray-700">
                If you don't receive the text within a few minutes, please double-check your number and try again or email us at <a href="mailto:contact@juneapp.ai" className="text-purple-600 underline">contact@juneapp.ai</a>
              </p>
              <Button
                onClick={handleResubmit}
                className="w-full border-2 border-[#881BC8] text-[#881BC8] bg-transparent hover:bg-purple-50 mt-2"
                style={{ fontWeight: 600 }}
              >
                Re-Submit Your Contact Details
              </Button>
              <p className="text-gray-500 text-small mt-4 text-center">Note: Interviews typically take about 5 to 10 minutes.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <h2 className="text-h4 text-gray-600 font-bold mb-2 text-left w-full">
                Enter your details to begin your interview
              </h2>
              <p className="mb-2 text-label text-gray-600 text-left w-full">
                Answer a few quick questions to move forward in the interview process. It's a fast and easy way to showcase your skills and help us connect with you sooner!
              </p>
              <p className="mb-4 text-gray-600 text-label font-semibold text-left w-full">
                Our system will send you an SMS message to start the screening interview with June AI.
              </p>
              <form onSubmit={handleSubmit} className="w-full flex flex-col items-center">
                {/* <div className="mb-4 w-full">
                <label className="block text-label text-gray-600 font-semibold mb-2">How would you like to complete your screening interview?</label>
                <div className="flex flex-col gap-2">
                  <label className="flex flex-col items-start cursor-pointer">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="channel"
                        value="sms"
                        checked={channel === "sms"}
                        onChange={() => setChannel("sms")}
                        className="hidden"
                      />
                      <span
                        className="w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2"
                        style={{
                          borderColor: channel === "sms" ? "#881BC8" : "#ccc",
                          background: channel === "sms" ? "#881BC8" : "#fff",
                          transition: "border-color 0.2s, background 0.2s",
                        }}
                      >
                        {channel === "sms" && (
                          <span
                            className="block w-3 h-3 rounded-full"
                            style={{ background: "#fff" }}
                          />
                        )}
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: "#222" }}
                      >
                        Text Message (SMS)
                      </span>
                    </div>
                    <span className="text-gray-500 text-small ml-6">We'll send you the questions via SMS—just reply when you're ready.</span>
                  </label>
                  <label className="flex flex-col items-start cursor-pointer">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="channel"
                        value="whatsapp"
                        checked={channel === "whatsapp"}
                        onChange={() => setChannel("whatsapp")}
                        className="hidden"
                      />
                      <span
                        className="w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2"
                        style={{
                          borderColor: channel === "whatsapp" ? "#881BC8" : "#ccc",
                          background: channel === "whatsapp" ? "#881BC8" : "#fff",
                          transition: "border-color 0.2s, background 0.2s",
                        }}
                      >
                        {channel === "whatsapp" && (
                          <span
                            className="block w-3 h-3 rounded-full"
                            style={{ background: "#fff" }}
                          />
                        )}
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: "#222" }}
                      >
                        WhatsApp
                      </span>
                    </div>
                    <span className="text-gray-500 text-small ml-6">We'll chat on WhatsApp so you can answer at your convenience.</span>
                  </label>
                </div>
              </div> */}
                <div className="mb-4 w-full">
                  <Label htmlFor="name" value="Name" />
                  <TextInput
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-4 w-full">
                  <Label htmlFor="phone" value="Phone Number (US Numbers Only)" />
                  <TextInput
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="555-123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full py-2 mt-2"
                  variant="primary"                >
                  Send Interview
                </Button>
                <p className="text-gray-500 text-small mt-4 text-center">Note: Interviews typically take about 5 to 10 minutes.</p>
              </form>
            </div>
          )}
        </Card>
      </div>
      {/* Footer */}
      <div className="w-full flex flex-col items-center mt-6 mb-4">
        <img src="/june logo png.png" alt="JUNE logo" className="h-6 mb-4" style={{ objectFit: 'contain' }} />
        <div className="text-small text-gray-500">
          Need Help? Email us at <a href="mailto:contact@juneapp.ai" className="text-purple-600 underline">contact@juneapp.ai</a>
        </div>
      </div>
    </div>
  );
};

export default InterviewPage;
